/*
 * The resource catalogue.
 *
 * Organisation names live here rather than in i18n.js. They are proper nouns
 * and every destination is an English-language site, so translating them would
 * mean maintaining three strings for a name the reader is about to see written
 * one way on arrival. Everything a reader reads *about* an entry — the group
 * title, the one-line description — is keyed off `id`/`key` and translated.
 *
 * `tone` sits on the group rather than being derived from its position, for the
 * reason ServicePage's TONE_CLASS comment records: index-based alternation means
 * inserting or reordering one section silently re-tints every section below it.
 *
 * Every URL below was fetched and confirmed live on 2026-07-27. Links rot, and
 * these are links a pregnant reader may follow at a bad moment — resources-config.test.js
 * pins each one so a silent edit fails the build rather than the reader.
 */
export const RESOURCE_GROUPS = [
  {
    id: "findCounsellor",
    tone: "plain",
    entries: [
      {
        key: "cbgc",
        name: "Canadian Board of Genetic Counselling — Find a Canadian Certified Genetic Counsellor",
        // The Canadian Association of Genetic Counsellors would be the obvious
        // entry here, but cagc-accg.ca is currently a "site under construction"
        // placeholder. CBGC certifies Canadian genetic counsellors and runs the
        // working directory, so it holds the slot; see CONTENT-TODO.md.
        //
        // A CMS query-string deep link, including the empty trailing parameter,
        // which is exactly the shape of URL that dies on a site rebuild.
        url: "https://www.cbgc-cccg.ca/index.php?page=41&id=",
      },
      {
        key: "nsgc",
        name: "NSGC Find a Genetic Counselor",
        url: "https://findageneticcounselor.nsgc.org/",
      },
      {
        key: "bcWomens",
        name: "BC Women's Hospital — Provincial Medical Genetics Program",
        url: "https://www.bcwomens.ca/our-services/medical-genetics",
      },
    ],
  },
  {
    id: "prenatalBc",
    tone: "tint",
    entries: [
      {
        key: "perinatalServicesBc",
        name: "BC Prenatal Genetic Screening — Perinatal Services BC",
        url: "https://www.perinatalservicesbc.ca/our-services/screening-programs/prenatal-genetic-screening",
      },
      {
        key: "gecKoPrenatalGuide",
        name: "GEC-KO — A Guide to Understanding Prenatal Screening",
        url: "https://www.geneticseducation.ca/resources-for-the-public/guide-to-understanding-prenatal-screening",
      },
      {
        key: "gecKoNipt",
        name: "GEC-KO — Non-Invasive Prenatal Testing",
        url: "https://www.geneticseducation.ca/resources-for-the-public/nipt-public",
      },
    ],
  },
  {
    id: "understandingGenetics",
    tone: "plain",
    entries: [
      {
        key: "medlinePlus",
        name: "MedlinePlus Genetics",
        url: "https://medlineplus.gov/genetics/",
      },
      {
        key: "gecKoBasics",
        name: "GEC-KO — Basic Genetics and Genomics",
        url: "https://www.geneticseducation.ca/resources-for-the-public/basic-genomics-public",
      },
      {
        key: "gard",
        name: "Genetic and Rare Diseases Information Center (GARD)",
        url: "https://rarediseases.info.nih.gov/",
      },
      {
        key: "gecKoGnda",
        name: "GEC-KO — Canada's Genetic Non-Discrimination Act",
        url: "https://www.geneticseducation.ca/resources-for-the-public/gnda-public",
      },
    ],
  },
  {
    id: "livingWithCondition",
    tone: "tint",
    entries: [
      {
        key: "rareDiseaseFoundation",
        name: "Rare Disease Foundation",
        url: "https://rarediseasefoundation.org/",
      },
      {
        key: "cord",
        name: "Canadian Organization for Rare Disorders",
        url: "https://www.raredisorders.ca/",
      },
      {
        key: "unique",
        name: "Unique — Rare Chromosome and Gene Disorder Support Group",
        url: "https://www.rarechromo.org/",
      },
    ],
  },
];

// The page is built but has not been audited, so it is held back from the live
// site. While this is false the navbar link is not rendered, the page is
// noindex, and the route sits behind the same soft gate as the other
// unpublished pages. Publishing is this one line; nothing else needs changing.
//
// The gate is a soft gate: see components/PasswordGate/PasswordGate.js. The
// page's content ships in the bundle either way, so this keeps the page from
// being found and linked, not from being read by someone determined.
export const RESOURCES_PUBLISHED = false;

// English-only, like every other `seo` block on the site: Helmet renders one
// document and og:locale is hardcoded en_CA. Kept beside the catalogue the way
// services-config.js keeps `seo` beside each service.
export const RESOURCES_SEO = {
  title: "Resources",
  description:
    "Where to find a genetic counsellor in Canada, what prenatal genetic screening British Columbia offers, and trusted places to read about genetic conditions and rare disease support.",
  keywords:
    "genetic counselling resources, find a genetic counsellor Canada, prenatal genetic screening British Columbia, rare disease support, genetic non-discrimination act",
  canonicalUrl: "/resources",
  ogUrl: "/resources",
};

export const getResourceGroup = (id) => RESOURCE_GROUPS.find((g) => g.id === id);

// The reader should be able to see where a link goes before taking it — the same
// promise the page's closing note makes in words. `new URL` throws on a
// malformed input, which would take the whole page down at render; the config
// test's "every url is absolute https" assertion is what keeps that impossible,
// so the two belong together.
export const hostOf = (url) => new URL(url).hostname.replace(/^www\./, "");
